import React from 'react';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';
import { IProps, IState } from './types';
import MemberLogin from 'components/Login/MemberLogin';
import { LoginStep } from 'constants/enums/loginStep.enum';
import Authentication from 'components/Login/Authentication';
import OTPVerification from 'components/Login/OTPVerification';
import SecurityQuestion from 'components/Login/SecurityQuestion';
import MemberVerifyComplete from 'components/Login/MemberVerifyComplete';
import { MemberLoginRes, OTPInfo, Captcha, LogInInfo } from 'bff/models/login';
import { MemberProfile, AddFavoriteItem } from 'bff/models/member';
import service from 'bff/services/loginService';
import apiCommonService from 'bff/services/commonService';
import storageService from 'utils/storageService';
import { storageKeys } from 'constants/enums/storageKeys.enum';
import memberProfileService from 'bff/services/memberProfileService';
import sweetAlertService from 'utils/sweetAlertService';
import { logOut } from 'redux/actions/components/member.action';
import { SactionType } from 'constants/enums/sactionType.enum';
import { fetchMyFavorite } from 'redux/actions/components/myFavorite.action';
import { SweetAlertIcon } from 'constants/enums/sweetAlertIcon.enum';
import { RootReducersState } from 'redux/reducers';
import { RouterSettings } from 'constants/models/routerSettings';
import commonService from 'utils/commonService';
import { DataLayerArgs } from 'react-gtm-module';
import { pushGTM } from 'plugins/gtmService';

class Login extends React.Component<IProps, IState> {
  state: IState = {
    captcha: {
      SN: '',
      CAPTCHA_CODE: ''
    },
    memberId: '',
    activeStep: LoginStep.MemberLogin,
    UICode: [],
    originUrl: ''
  }

  componentDidMount = async () => {
    // 若已完成我的收藏載入，視為已登入，先進行登出
    if (this.props.fetchFavoriteComplete) {
      this.props.onLogOut();
    }

    let originUrlVal = '';
    if (this.props.history.location.state) {
      const { originUrl }: any = this.props.history.location.state;
      originUrlVal = originUrl || '';
    }

    const initSettingRes = await service.fetchInitSetting();
    this.setState({
      captcha: { ...initSettingRes.captcha },
      UICode: [...initSettingRes.UICode],
      originUrl: originUrlVal
    });
  }

  componentDidUpdate () {
    // console.log('Login componentDidUpdate');
    if (this.props.fetchFavoriteComplete) {
      if (this.state.originUrl) {
        this.props.history.push(this.state.originUrl);
        return;
      }

      // 如果路由有狀態，且有記錄前次路由，表為 header 導頁而來，導頁回原本網址
      if (this.props.history.location.state) {
        const { preUrl }: any = this.props.history.location.state;
        if (preUrl) {
          const lastIndex = preUrl.lastIndexOf('/');
          const targetUrl = preUrl.substring(lastIndex);
          this.props.history.push(`${process.env.REACT_APP_PREFIX}${targetUrl}`);
          return;
        }
      }
      this.props.history.push(RouterSettings.Home);
    }
    commonService.scrollToTop();
  }

  componentWillUnmount () {
    this.handleSetActivePage(LoginStep.MemberLogin);
  }

  /**
   * @description 切換登入流程頁面
   */
  handleSetActivePage = (step: LoginStep) => {
    this.setState({
      activeStep: step
    });
  }

  /**
   * @description 會員登入 Page 1 取得驗證碼方法
   */
  handleFetchCaptcha = async () => {
    const captcha = await service.fetchCaptcha();
    this.setState({
      captcha: { ...captcha as Captcha }
    });
  }

  /**
   * @description 會員登入 Page 1 進行登入檢核方法
   * @param memberId 會員登入帳號
   * @param memberLoginRes 登入檢核結果-會員姓名、手機、 Email 資訊
   */
  handleMemberLogin = async (memberId: string, memberLoginRes: MemberLoginRes) => {
    this.setState({
      activeStep: LoginStep.Authentication,
      memberId: memberId,
      memberLoginInfo: { ...memberLoginRes }
    });
  };

  /**
   * @description 從選取驗證方式頁返回登入頁，重新刷新驗證碼
  */
  handleGoBackMemberLogin = async () => {
    const captcha = await service.fetchCaptcha();
    this.setState({
      activeStep: LoginStep.MemberLogin,
      captcha: { ...captcha as Captcha }
    });
  }

  /**
   * @description 會員登入 Page 2 取得 OTP 驗證碼方法
   * @param memberProfile 會員資料
   * @param updateOTP 是否重新更新 OTP
   */
  handleFetchOTP = async (memberProfile: MemberProfile, updateOTP?: boolean) => {
    if (!updateOTP) return;

    const result = await apiCommonService.fetchOTP(memberProfile, SactionType.Login);
    if (result) {
      this.setState({
        activeStep: LoginStep.OTPVerification,
        memberVerifyInfo: { ...memberProfile },
        otpInfo: { ...result.TRANRS },
        getOTPTime: new Date().getTime()
      });
    }
  };

  /**
   * @description 會員登入 Page 2 重新取得 OTP 驗證碼方法
   */
  handleResentOTP = async () => {
    const result = await apiCommonService.fetchOTP(this.state.memberVerifyInfo as MemberProfile, SactionType.Login, true);
    if (result) {
      this.setState({
        otpInfo: { ...result.TRANRS },
        getOTPTime: new Date().getTime()
      });
    }
  };

  /**
   * @description 會員登入 Page 3 驗證 OTP認證碼
   * @param otpVerificationReq OTP 驗證頁 Request Model
   */
  handleVerifyOTP = async (otpCode: string) => {
    const result = await apiCommonService.verifyOTP(this.state.otpInfo as OTPInfo, otpCode, SactionType.Login, true);
    if (!result) return;

    storageService.removeItem(storageKeys.MemberId);
    storageService.removeItem(storageKeys.LoginInfo);
    commonService.storageData(storageKeys.MemberId, this.state.memberId);
    commonService.storageData(storageKeys.LoginInfo, result);

    let addFavoriteItem;
    if (this.props.unloginAddItem.InsTypeId && this.props.unloginAddItem.PlanId && this.props.unloginAddItem.ProdId) {
      addFavoriteItem =
        commonService.getAddFavoriteItem(this.props.unloginAddItem.InsTypeId, this.props.unloginAddItem.ProdId, this.props.unloginAddItem.PlanId);
    }

    this.props.onFetchMyFavorite(this.state.memberId, addFavoriteItem);

    this.setState({
      getOTPTime: 0
    });

    pushGTM(this.getGtmArg('login'));
  };

  /**
   * @description 會員登入 驗證安全問題查核
   * @param validSecqueReq 會員驗證問題查核 Request Model
   */
  handleFetchSecque = async () => {
    const secque = await service.fetchMemberSecque(this.state.memberId);

    if (secque && secque.SECQUE.SECQUE_ID && secque.SECQUE.SECQUE_QUESTION) {
      this.setState({
        activeStep: LoginStep.SecurityQuestion,
        secque: secque
      });
    } else {
      sweetAlertService.modal({
        icon: SweetAlertIcon.Info,
        title: '無法取得安全問題',
        html: '於上班日周一至週五08:30-17:30來電洽0800-024-024取得其他登入方式，謝謝您。'
      });
    }
  };

  /**
   * @description 會員安全問題查核通過，導頁至會員維護頁
  */
  handleMemberVerifyComplete = () => {
    const path = {
      pathname: RouterSettings.MemberCenter.MemberMaintain,
      state: { preUrl: RouterSettings.Login, originUrl: this.state.originUrl }
    };
    this.props.history.push(path);
  }

  getGtmArg = (event:string): DataLayerArgs => {
    // 取得userID
    const loginInfo = JSON.parse(storageService.getItem(storageKeys.LoginInfo)) as LogInInfo;
    return {
      dataLayer: {
        event: 'td_login',
        UserID: loginInfo.sub
      }
    };
  }

  render () {
    let activePageElement;
    switch (this.state.activeStep) {
      case LoginStep.Authentication: // 身份驗證頁
        activePageElement = (
          <Authentication
            memberId={this.state.memberId}
            memberInfo={this.state.memberLoginInfo as MemberLoginRes}
            onSubmit={this.handleFetchOTP}
            onGoBack={() => this.handleGoBackMemberLogin()}
          />
        );
        break;
      case LoginStep.OTPVerification: // OTP驗證頁
        activePageElement = (
          <OTPVerification
            memberId={this.state.memberId}
            otpInfo={this.state.otpInfo as OTPInfo}
            getOTPTime={this.state.getOTPTime as number}
            UICode={this.state.UICode}
            onResentOTP={() => this.handleResentOTP()}
            onVerifyOTP={this.handleVerifyOTP}
            onFetchSecque={this.handleFetchSecque}
            onGoBack={() => this.handleSetActivePage(LoginStep.Authentication)}
          />
        );
        break;
      case LoginStep.SecurityQuestion: // 詢問安全問題頁
        activePageElement = (
          <SecurityQuestion
            secque={this.state.secque}
            memberId={this.state.memberId}
            UICode={this.state.UICode}
            onVerifySuccess={() => this.handleSetActivePage(LoginStep.MemberVerifyComplete)}
            onGoBack={() => this.handleSetActivePage(LoginStep.OTPVerification)}
          />
        );
        break;
      case LoginStep.MemberVerifyComplete: // 修改會員資料提示頁
        commonService.storageData(storageKeys.MemberId, this.state.memberId);
        activePageElement = (
          <MemberVerifyComplete
            onSubmit={this.handleMemberVerifyComplete}
          />
        );
        break;
      case LoginStep.MemberLogin: // 會員登入頁
      default:
        activePageElement = (
          <MemberLogin
            captcha={this.state.captcha}
            UICode={this.state.UICode}
            originUrl={this.state.originUrl}
            onSubmit={this.handleMemberLogin}
            onCaptchaChange={this.handleFetchCaptcha}
          />
        );
        break;
    }

    return (
      <div className="login">
        {activePageElement}
      </div>
    );
  }
};

const mapStateToProps = (state: RootReducersState) => {
  return {
    fetchFavoriteComplete: state.myFavorite.fetchFavoriteComplete,
    unloginAddItem: state.myFavorite.unloginAddItem
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => {
  return {
    onFetchMyFavorite: (memberId: string, addItem?: AddFavoriteItem) => dispatch(fetchMyFavorite(memberId, addItem)),
    onLogOut: () => dispatch(logOut())
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(Login);
